import React, { useState } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../../lib/api.js';

/**
 * DispositionModal — Post-call outcome logging modal.
 * Enforces outcome selection on calls >= 20s.
 */
export default function DispositionModal({ callId, durationSec = 0, onClose }) {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const REQUIRED_MIN_SEC = 20;
  const isRequired = (durationSec || 0) >= REQUIRED_MIN_SEC;

  const outcomes = [
    { id: 'connected', label: '✅ Connected', color: 'text-green-400' },
    { id: 'booked', label: '📅 Booked Appointment', color: 'text-civix-purpleSoft' },
    { id: 'callback', label: '📞 Callback', color: 'text-blue-400' },
    { id: 'no-answer', label: '📵 No Answer', color: 'text-yellow-400' },
    { id: 'voicemail', label: '🎙 Left Voicemail', color: 'text-orange-400' },
    { id: 'not-interested', label: '🚫 Not Interested', color: 'text-red-400' },
    { id: 'wrong-number', label: '❌ Wrong Number', color: 'text-white/50' },
  ];

  const formatDuration = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  async function save() {
    if (!outcome) { toast.error('Select an outcome'); return; }
    setSaving(true);
    try {
      await apiPost(`/phone/calls/${callId}/disposition`, { outcome, notes });
      toast.success('Call logged');
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
      qc.invalidateQueries({ queryKey: ['call-history'] });
      qc.invalidateQueries({ queryKey: ['call-history-today'] });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  }

  return (
    <div
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-xs"
      onClick={(e) => { if (!isRequired && e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-civix-panel border border-civix-border rounded-2xl p-6 shadow-panel w-96 max-w-[95vw]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-white">
              {isRequired ? 'Log Call (required)' : 'Log Call'}
            </h3>
            {durationSec > 0 && (
              <div className="text-xs text-white/40">
                Duration: {formatDuration(durationSec)}
                {isRequired && (
                  <span className="ml-2 text-yellow-300/80">• disposition required</span>
                )}
              </div>
            )}
          </div>
          {!isRequired && (
            <button onClick={onClose} className="text-white/40 hover:text-white transition">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="space-y-2 mb-4">
          {outcomes.map((o) => (
            <button
              key={o.id}
              onClick={() => setOutcome(o.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border transition text-sm ${
                outcome === o.id
                  ? 'border-civix-purple bg-civix-purple/20 text-white'
                  : 'border-civix-border bg-civix-panelLight text-white/70 hover:border-civix-border/80'
              } ${o.color}`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)…"
          rows={2}
          className="w-full bg-civix-panelLight border border-civix-border rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple transition resize-none mb-4"
        />

        <div className="flex gap-2">
          {!isRequired && (
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-civix-border text-white/60 hover:text-white text-sm transition">
              Skip
            </button>
          )}
          <button
            onClick={save}
            disabled={!outcome || saving}
            className={`${isRequired ? 'w-full' : 'flex-1'} py-2.5 rounded-xl bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2 transition`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Save
          </button>
        </div>
        {isRequired && (
          <div className="mt-3 text-[10px] text-white/40 text-center">
            You must log an outcome before continuing. Every &ge;20s call needs a disposition.
          </div>
        )}
      </div>
    </div>
  );
}
